import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  purchaseApi,
  vendorApi,
  inventoryApi,
  categoryApi,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import Modal from "../components/Modal";
import {
  FiPlus,
  FiTrash2,
  FiEdit,
  FiSearch,
  FiCheckCircle,
  FiTruck,
  FiCopy,
  FiX,
  FiChevronDown,
  FiUpload,
  FiDownload,
  FiFileText,
  FiClock,
  FiEye,
  FiCalendar,
  FiBox,
  FiLayers,
  FiChevronLeft,
  FiChevronRight,
  FiRotateCcw,
  FiShoppingCart,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import PurchaseReturnsTab from "../components/PurchaseReturnsTab";


// Searchable Dropdown for Selecting Vendor Supplier
const SearchableVendorSelect = ({
  vendors,
  selectedVendorId,
  onSelect,
  placeholder = "-- Choose or search vendor supplier --",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedVendor = vendors.find((v) => String(v.id) === String(selectedVendorId));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const filteredVendors = vendors.filter((v) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (v.company_name && v.company_name.toLowerCase().includes(term)) ||
      (v.contact_person_name && v.contact_person_name.toLowerCase().includes(term)) ||
      (v.contact && v.contact.toLowerCase().includes(term)) ||
      (v.email && v.email.toLowerCase().includes(term)) ||
      (v.ntn && v.ntn.toLowerCase().includes(term)) ||
      (v.city && v.city.toLowerCase().includes(term)) ||
      (v.address && v.address.toLowerCase().includes(term))
    );
  });

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          height: 42,
          padding: "9px 14px",
          borderRadius: 8,
          border: selectedVendor ? "1.5px solid var(--primary, #064E3B)" : "1.5px solid var(--slate-300)",
          backgroundColor: "#fff",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontSize: "0.86rem",
          fontWeight: selectedVendor ? 600 : 400,
          color: selectedVendor ? "var(--navy)" : "var(--slate-400)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          boxSizing: "border-box",
          transition: "border-color 0.15s ease",
        }}
      >
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <FiTruck size={15} style={{ color: selectedVendor ? "var(--primary)" : "var(--slate-400)", flexShrink: 0 }} />
          {selectedVendor ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>{selectedVendor.company_name}</span>
              {selectedVendor.contact_person_name && (
                <span style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 500 }}>
                  ({selectedVendor.contact_person_name})
                </span>
              )}
              {selectedVendor.contact && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "var(--slate-100)",
                    color: "var(--slate-600)",
                    padding: "1px 6px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}
                >
                  {selectedVendor.contact}
                </span>
              )}
            </div>
          ) : (
            <span style={{ color: "var(--slate-400)" }}>{placeholder}</span>
          )}
        </div>
        <FiChevronDown
          size={16}
          style={{
            color: "var(--slate-400)",
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: "#fff",
            border: "1px solid var(--slate-200)",
            borderRadius: 10,
            boxShadow: "0 14px 35px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.05)",
            maxHeight: 320,
            overflowY: "auto",
            minWidth: 300,
          }}
        >
          {/* Sticky Search Header */}
          <div
            style={{
              padding: 10,
              borderBottom: "1px solid var(--slate-100)",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 10,
            }}
          >
            <div style={{ position: "relative" }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search vendor by company, contact person, phone, NTN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "7px 28px 7px 32px",
                  borderRadius: 6,
                  border: "1.5px solid var(--primary, #064E3B)",
                  fontSize: "0.82rem",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--slate-400)",
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Vendors List */}
          <div style={{ padding: 6 }}>
            {filteredVendors.length === 0 ? (
              <div style={{ padding: "16px", textAlign: "center", fontSize: "0.82rem", color: "var(--slate-400)" }}>
                No matching vendors found
              </div>
            ) : (
              filteredVendors.map((v) => {
                const isSelected = String(v.id) === String(selectedVendorId);
                return (
                  <div
                    key={v.id}
                    onClick={() => {
                      onSelect(String(v.id));
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    style={{
                      padding: "9px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      borderBottom: "1px solid var(--slate-50)",
                      backgroundColor: isSelected ? "var(--primary-light, #ECFDF5)" : "transparent",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = "var(--slate-100)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 800, color: "var(--navy)" }}>{v.company_name}</span>
                        {isSelected && (
                          <span
                            style={{
                              fontSize: "0.68rem",
                              backgroundColor: "var(--primary, #064E3B)",
                              color: "#fff",
                              padding: "1px 5px",
                              borderRadius: 4,
                              fontWeight: 700,
                            }}
                          >
                            Selected
                          </span>
                        )}
                      </div>
                      {v.contact && (
                        <span
                          style={{
                            fontSize: "0.72rem",
                            backgroundColor: "var(--slate-100)",
                            color: "var(--slate-700)",
                            padding: "1px 6px",
                            borderRadius: 4,
                            fontWeight: 600,
                          }}
                        >
                          {v.contact}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, fontSize: "0.74rem", color: "var(--slate-500)", marginTop: 3, flexWrap: "wrap" }}>
                      {v.contact_person_name && (
                        <span>
                          Contact: <strong style={{ color: "var(--slate-700)" }}>{v.contact_person_name}</strong>
                        </span>
                      )}
                      {v.email && <span>• {v.email}</span>}
                      {v.ntn && <span>• NTN: <code style={{ color: "var(--navy)" }}>{v.ntn}</code></span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable & Autocomplete Item Name Input (Filters by Category & Subcategory + Free Typing for New Items)
const ItemNameAutocomplete = ({
  value,
  onChange,
  onSelectItem,
  categoryId,
  categoryName,
  subcategoryId,
  subcategoryName,
  knownCatalog = [],
  placeholder = "e.g. Kingston NV2 512GB",
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter items based on category, subcategory, and search term
  const suggestions = useMemo(() => {
    const term = (value || "").trim().toLowerCase();

    // 1. Filter by category
    const catFiltered = (knownCatalog || []).filter((it) => {
      if (categoryId) {
        return String(it.category_id) === String(categoryId);
      }
      return true;
    });

    // 2. Score subcategory match
    let scored = catFiltered.map((it) => {
      let isSubMatch = false;
      if (subcategoryId && it.subcategory_id) {
        isSubMatch = String(it.subcategory_id) === String(subcategoryId);
      } else if (!subcategoryId && !it.subcategory_id) {
        isSubMatch = true;
      }
      return {
        ...it,
        isSubMatch,
      };
    });

    // 3. Filter by search query if typed
    if (term) {
      scored = scored.filter(
        (it) =>
          it.item_name.toLowerCase().includes(term) ||
          (it.subcategory_name && it.subcategory_name.toLowerCase().includes(term)) ||
          (it.sku && it.sku.toLowerCase().includes(term))
      );
    }

    // 4. Sort: subcategory exact match first, then alphabetical
    scored.sort((a, b) => {
      if (a.isSubMatch && !b.isSubMatch) return -1;
      if (!a.isSubMatch && b.isSubMatch) return 1;
      return a.item_name.localeCompare(b.item_name);
    });

    return scored.slice(0, 30);
  }, [knownCatalog, categoryId, subcategoryId, value]);

  const isExactMatch = suggestions.some(
    (s) => s.item_name.trim().toLowerCase() === (value || "").trim().toLowerCase()
  );

  const handleSelect = (item) => {
    onSelectItem(item);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      if (isOpen && highlightIndex >= 0 && suggestions[highlightIndex]) {
        e.preventDefault();
        handleSelect(suggestions[highlightIndex]);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setHighlightIndex(-1);
    } else if (e.key === "Tab") {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        required
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!isOpen) setIsOpen(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          padding: "6px 8px",
          borderRadius: 6,
          border: hasError ? "1.5px solid #F59E0B" : "1px solid var(--slate-300)",
          fontSize: "0.82rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
        }}
      />

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 99999,
            backgroundColor: "#fff",
            border: "1.5px solid #CBD5E1",
            borderRadius: 8,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.08)",
            maxHeight: 250,
            overflowY: "auto",
            minWidth: 280,
            width: "max-content",
            maxWidth: 380,
          }}
        >
          {/* Header showing scope */}
          <div
            style={{
              padding: "6px 10px",
              backgroundColor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#475569",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              {categoryName ? `In ${categoryName}` : "All Categories"}
              {subcategoryName ? ` • ${subcategoryName}` : ""}
            </span>
            <span style={{ fontSize: "0.68rem", color: "#94A3B8" }}>
              {suggestions.length} items found
            </span>
          </div>

          {/* New Item Option if user typed something not matching */}
          {value && value.trim() && !isExactMatch && (
            <div
              onClick={() => {
                setIsOpen(false);
              }}
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid #E2E8F0",
                backgroundColor: "#F0FDF4",
                cursor: "pointer",
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: "#166534",
                fontWeight: 600,
              }}
              title="Click or press Tab/Enter to keep this as a new item"
            >
              <FiPlus size={14} style={{ color: "#16A34A", flexShrink: 0 }} />
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                Add new item: <strong>&ldquo;{value.trim()}&rdquo;</strong>
              </div>
            </div>
          )}

          {/* Existing suggestions list */}
          {suggestions.length === 0 ? (
            <div style={{ padding: "12px 10px", fontSize: "0.78rem", color: "#64748B", textAlign: "center" }}>
              No existing items found for this category. You can type freely to add as new.
            </div>
          ) : (
            suggestions.map((item, i) => {
              const isHighlighted = i === highlightIndex;
              return (
                <div
                  key={`${item.item_name}-${i}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightIndex(i)}
                  style={{
                    padding: "7px 10px",
                    borderBottom: "1px solid #F1F5F9",
                    backgroundColor: isHighlighted ? "#EFF6FF" : "#fff",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    transition: "background-color 0.1s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 700, color: isHighlighted ? "#1D4ED8" : "#0F172A" }}>
                      {item.item_name}
                    </span>
                    {item.unit_price && parseFloat(item.unit_price) > 0 && (
                      <span style={{ fontSize: "0.72rem", color: "#059669", fontWeight: 700, flexShrink: 0 }}>
                        Rs. {parseFloat(item.unit_price).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, fontSize: "0.7rem", color: "#64748B" }}>
                    {item.subcategory_name && (
                      <span
                        style={{
                          backgroundColor: item.isSubMatch ? "#DBEAFE" : "#F1F5F9",
                          color: item.isSubMatch ? "#1E40AF" : "#475569",
                          padding: "1px 5px",
                          borderRadius: 3,
                          fontWeight: item.isSubMatch ? 700 : 500,
                        }}
                      >
                        {item.subcategory_name}
                      </span>
                    )}
                    {item.sku && <span>SKU: {item.sku}</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const PurchasesPage = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || (Array.isArray(user?.permissions) && user.permissions.includes("purchases"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active Tab: 'record' (Record Vendor Purchase) or 'history' (Purchase History) or 'returns' (Purchase Returns)
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "history"
      ? "history"
      : searchParams.get("tab") === "returns"
      ? "returns"
      : "record"
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "history" || tab === "record" || tab === "returns") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Purchase History State
  const [purchases, setPurchases] = useState([]);
  const [historyPurchases, setHistoryPurchases] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState("");
  const [billToDelete, setBillToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyPagination, setHistoryPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // Record Vendor Purchase Form State
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split("T")[0]);
  const [billNumber, setBillNumber] = useState("");
  const [notes, setNotes] = useState("");

  // State for purchase items rows
  const [generatedRows, setGeneratedRows] = useState([]);
  const fileInputRef = useRef(null);

  // Edit Purchase State (for editing on the main Record tab)
  const [editingPurchase, setEditingPurchase] = useState(null);

  // Modals & submission state
  const [submitting, setSubmitting] = useState(false);

  const populatePurchaseForEdit = (
    billData,
    currentCats = categories,
    currentSubs = subcategories,
    currentVendors = vendors
  ) => {
    if (!billData) return;

    // Resolve vendor ID with fallback match by vendor_name
    let targetVendorId = billData.vendor_id ? String(billData.vendor_id) : "";
    if (!targetVendorId && billData.vendor_name) {
      const vList = currentVendors && currentVendors.length > 0 ? currentVendors : vendors;
      const matchedVendor = vList.find(
        (v) =>
          String(v.company_name).trim().toLowerCase() === String(billData.vendor_name).trim().toLowerCase()
      );
      if (matchedVendor) {
        targetVendorId = String(matchedVendor.id);
      }
    }
    setSelectedVendorId(targetVendorId);

    setPurchaseDate(
      billData.purchase_date
        ? String(billData.purchase_date).slice(0, 10)
        : billData.created_at
        ? String(billData.created_at).slice(0, 10)
        : new Date().toISOString().split("T")[0]
    );
    setBillNumber(billData.bill_number || "");
    setNotes(billData.notes || "");

    const rawItems = billData.items || [];
    if (rawItems.length > 0) {
      setGeneratedRows(
        rawItems.map((it, idx) => {
          const matchedCat = currentCats.find((c) => c.id === it.category_id) || currentCats[0];
          const subs =
            matchedCat?.values && matchedCat.values.length > 0
              ? matchedCat.values
              : currentSubs.filter((s) => s.category_id === matchedCat?.id);
          const matchedSub = subs.find(
            (s) => s.id === (it.subcategory_id || it.sub_category_id)
          ) || subs[0] || { id: null, name: matchedCat?.name || "" };

          return {
            id: it.id,
            rowId: `pur-row-edit-${it.id || idx}-${Date.now()}`,
            category_id: it.category_id || matchedCat?.id || null,
            category_name: it.category_name || matchedCat?.name || "Hardware",
            subcategory_id: it.subcategory_id || it.sub_category_id || matchedSub?.id || null,
            value_name: it.subcategory_name || it.value_name || matchedSub?.name || "",
            item_name: it.item_name || "",
            quantity: it.quantity ? Math.max(1, parseInt(it.quantity)) : 1,
            unit_price: it.price !== undefined && it.price !== null ? it.price : (it.unit_price || ""),
            sale_price: it.sale_price !== null && it.sale_price !== undefined ? it.sale_price : "",
            serial_number: it.serial_number || "",
            part_number: it.part_number || "",
            sku: it.sku || "",
            warranty_start_date: it.warranty_start || it.warranty_start_date || "",
            warranty_end_date: it.warranty_end || it.warranty_end_date || "",
          };
        })
      );
    }

    setEditingPurchase({
      id: billData.id,
      originalBillNumber: billData.bill_number,
    });
    setActiveTab("record");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadBillForEdit = async (
    billIdOrNo,
    billObj = null,
    currentCats = categories,
    currentSubs = subcategories,
    currentVendors = vendors
  ) => {
    try {
      let fullBill = billObj;
      if (
        !fullBill ||
        !fullBill.vendor_id ||
        !Array.isArray(fullBill.items) ||
        fullBill.items.length === 0
      ) {
        const res = await purchaseApi.getById(billIdOrNo);
        fullBill = res?.data || res;
      }
      if (fullBill) {
        populatePurchaseForEdit(fullBill, currentCats, currentSubs, currentVendors);
      }
    } catch (err) {
      console.error("Error loading purchase bill for edit:", err);
      toast.error("Could not load purchase bill for editing.");
    }
  };

  const handleResetForm = () => {
    setEditingPurchase(null);
    setSelectedVendorId("");
    setPurchaseDate(new Date().toISOString().split("T")[0]);
    setBillNumber("");
    setNotes("");
    setGeneratedRows([]);
  };

  const handleCancelEdit = () => {
    handleResetForm();
    setSearchParams({ tab: "record" });
    toast.info("Edit mode cancelled. Form reset.");
  };

  const fetchData = async (forceSkipEdit = false) => {
    try {
      setLoading(true);
      const [vendorsRes, itemsRes, categoriesRes, subcategoriesRes, purchasesRes] =
        await Promise.all([
          vendorApi.getAll(),
          inventoryApi.getAll(),
          categoryApi.getCategories(),
          categoryApi.getSubcategories(),
          purchaseApi.getAll(),
        ]);

      const fetchedVendors = vendorsRes.data || [];
      const fetchedItems = itemsRes.data || [];
      const fetchedCats = categoriesRes.data || [];
      const fetchedSubs = subcategoriesRes.data || [];
      const fetchedPurchases = purchasesRes.data || [];

      setVendors(fetchedVendors);
      setItems(fetchedItems);
      setCategories(fetchedCats);
      setSubcategories(fetchedSubs);
      setPurchases(fetchedPurchases);

      // Check if URL has editBill or editId
      const editBillParam = !forceSkipEdit
        ? (searchParams.get("editBill") || searchParams.get("editId"))
        : null;
      if (editBillParam) {
        const found = fetchedPurchases.find(
          (p) =>
            String(p.bill_number).trim().toLowerCase() === String(editBillParam).trim().toLowerCase() ||
            String(p.id) === String(editBillParam)
        );
        loadBillForEdit(editBillParam, found, fetchedCats, fetchedSubs, fetchedVendors);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load purchase data");
    } finally {
      setLoading(false);
    }
  };

  // Catalog of known unique items across inventory and purchases for item name suggestions
  const knownItemsCatalog = useMemo(() => {
    const map = new Map();

    const addItem = (it) => {
      const name = (it.item_name || it.name || "").trim();
      if (!name) return;
      const catId = it.category_id ? String(it.category_id) : "";
      const subId = it.subcategory_id || it.sub_category_id ? String(it.subcategory_id || it.sub_category_id) : "";

      const catObj = categories.find((c) => String(c.id) === catId);
      const subObj = subcategories.find((s) => String(s.id) === subId);
      const catName = it.category_name || catObj?.name || "";
      const subName = it.subcategory_name || it.value_name || subObj?.name || "";

      const key = `${catId}_${subId}_${name.toLowerCase()}`;

      if (!map.has(key)) {
        map.set(key, {
          item_name: name,
          category_id: it.category_id || catObj?.id || null,
          category_name: catName,
          subcategory_id: it.subcategory_id || it.sub_category_id || subObj?.id || null,
          subcategory_name: subName,
          unit_price: it.price !== undefined && it.price !== null ? it.price : (it.unit_price || ""),
          sale_price: it.sale_price !== null && it.sale_price !== undefined ? it.sale_price : "",
          sku: it.sku || "",
          part_number: it.part_number || "",
        });
      }
    };

    (items || []).forEach(addItem);

    (purchases || []).forEach((p) => {
      if (Array.isArray(p.items)) {
        p.items.forEach(addItem);
      } else if (p.item_name) {
        addItem(p);
      }
    });

    return Array.from(map.values());
  }, [items, purchases, categories, subcategories]);

  // Group purchase records by bill_number (each bill contains all items purchased under that bill)
  const groupedBills = useMemo(() => {
    const map = new Map();
    (purchases || []).forEach((p) => {
      const billKey =
        p.bill_number && String(p.bill_number).trim() !== ""
          ? String(p.bill_number).trim()
          : `BILL-LEGACY-${p.id}`;

      if (!map.has(billKey)) {
        map.set(billKey, {
          id: p.id,
          vendor_id: p.vendor_id || (p.items && p.items[0]?.vendor_id) || null,
          bill_number: p.bill_number || `BILL-LEGACY-${p.id}`,
          display_bill_number: p.bill_number || "—",
          has_bill_number: Boolean(p.bill_number && String(p.bill_number).trim() !== ""),
          purchase_date: p.purchase_date || p.created_at,
          vendor_name: p.vendor_name || "—",
          vendor_contact: p.vendor_contact || "",
          vendor_contact_person: p.vendor_contact_person || "",
          created_by_name: p.created_by_name || "",
          created_at: p.created_at,
          items: [],
          total_price: 0,
          total_quantity: 0,
        });
      }
      const billObj = map.get(billKey);
      const returnedAmt = parseFloat(p.total_returned_amount) || parseFloat(p.returned_amount) || 0;
      const returnedItemsCount = parseInt(p.total_returned_items_count) || 0;

      if (Array.isArray(p.items) && p.items.length > 0) {
        billObj.items.push(...p.items);
        const unitsCount = p.items.reduce((s, it) => s + (parseInt(it.quantity) || 1), 0);
        billObj.total_quantity += unitsCount;

        const activeItemsSum = p.items.reduce((s, it) => s + (parseFloat(it.price || it.unit_price) || 0) * (parseInt(it.quantity) || 1), 0);
        billObj.total_price += activeItemsSum;
      } else {
        if (returnedAmt > 0) {
          billObj.total_quantity += 0;
          billObj.total_price += 0;
        } else {
          const qty = parseInt(p.quantity) || parseInt(p.total_items_count) || 0;
          const tPrice = parseFloat(p.total_amount) || parseFloat(p.total_price) || 0;
          billObj.total_price += tPrice;
          billObj.total_quantity += qty;
        }
      }

      if (returnedAmt > 0) {
        billObj.returned_amount = (billObj.returned_amount || 0) + returnedAmt;
        billObj.returned_items = (billObj.returned_items || 0) + returnedItemsCount;
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const dateA = new Date(a.purchase_date || a.created_at || 0).getTime();
      const dateB = new Date(b.purchase_date || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [purchases]);

  // Debounce search query to prevent frequent API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHistorySearch(historySearch);
      setHistoryPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [historySearch]);

  const fetchPurchaseHistory = async (
    page = historyPage,
    limit = historyPageSize,
    search = debouncedHistorySearch
  ) => {
    try {
      setHistoryLoading(true);
      const params = {
        page,
        limit,
      };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const res = await purchaseApi.getAll(params);
      const resPagination = res?.pagination || res?.data?.pagination;
      const resData = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

      setHistoryPurchases(resData);
      if (resPagination) {
        setHistoryPagination(resPagination);
      } else {
        setHistoryPagination({
          total: resData.length,
          page,
          limit,
          totalPages: Math.ceil(resData.length / limit) || 1,
        });
      }
    } catch (err) {
      console.error("Fetch purchase history error:", err);
      toast.error("Failed to load purchase history from server");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchPurchaseHistory(historyPage, historyPageSize, debouncedHistorySearch);
    }
  }, [activeTab, debouncedHistorySearch, historyPage, historyPageSize]);

  // Pagination calculations for Purchase History based on backend data
  const totalBills = historyPagination.total || 0;
  const totalHistoryPages = historyPagination.totalPages || 1;
  const historyStartIndex = totalBills === 0 ? 0 : (historyPagination.page - 1) * historyPagination.limit + 1;
  const historyEndIndex = Math.min(
    (historyPagination.page - 1) * historyPagination.limit + historyPurchases.length,
    totalBills
  );

  const handleViewInvoice = (bill) => {
    navigate(`/purchases/invoice/${encodeURIComponent(bill.bill_number)}`, {
      state: { bill },
    });
  };

  const handleConfirmDelete = async () => {
    if (!billToDelete) return;
    try {
      setDeleting(true);
      const idToDelete = billToDelete.id || billToDelete.bill_number;
      const res = await purchaseApi.delete(idToDelete);
      toast.success(res.data?.message || `Purchase bill #${billToDelete.bill_number} and all related items deleted`);
      setIsDeleteModalOpen(false);
      setBillToDelete(null);
      fetchPurchaseHistory();
      fetchData(true);
    } catch (err) {
      console.error("Delete purchase error:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to delete purchase bill");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const flashMsg = sessionStorage.getItem("purchase_success_msg");
    if (flashMsg) {
      toast.success(flashMsg);
      sessionStorage.removeItem("purchase_success_msg");
    }
    fetchData();
  }, []);

  // Helper to generate SKU
  const generateSku = (catName, valName, index) => {
    const cPrefix = (catName || "IT").slice(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, "");
    const vPrefix = (valName || "GEN").slice(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, "");
    const randomPart = Math.floor(1000 + Math.random() * 9000);
    const timestamp = Date.now().toString().slice(-4);
    return `${cPrefix}-${vPrefix}-${timestamp}${index + 1}-${randomPart}`;
  };

  // Helper to identify if category is bulk (non-serialized / accessories / software)
  const isBulkCategory = (catId, catName) => {
    const cat = categories.find((c) => String(c.id) === String(catId));
    if (cat && (cat.is_serialized === 0 || cat.is_serialized === false || cat.category_type === "bulk")) {
      return true;
    }
    const name = (catName || cat?.name || "").toLowerCase();
    const bulkKeywords = ["ram", "nvme", "mouse", "mouse pad", "pad", "bag", "license", "office", "windows", "keyboard"];
    return bulkKeywords.some((k) => name.includes(k));
  };

  // Helper to create an empty row with default or specified category
  const createEmptyRow = (catId = null, subcatId = null) => {
    const targetCat = catId
      ? categories.find((c) => String(c.id) === String(catId))
      : categories[0];
    const cId = targetCat ? targetCat.id : null;
    const cName = targetCat ? targetCat.name : "";
    const subs = targetCat?.values && targetCat.values.length > 0
      ? targetCat.values
      : subcategories.filter((s) => s.category_id === cId);
    const targetSub = subcatId
      ? subs.find((s) => String(s.id) === String(subcatId))
      : (subs[0] || { id: null, name: cName });

    return {
      rowId: `pur-row-${Date.now()}-${Math.random()}`,
      category_id: cId,
      category_name: cName,
      subcategory_id: targetSub ? targetSub.id : null,
      value_name: targetSub ? targetSub.name : cName,
      item_name: "",
      quantity: 1,
      min_stock_alert: 5,
      unit_price: "",
      sale_price: "",
      serial_number: "",
      part_number: "",
      sku: "",
      warranty_start_date: purchaseDate || new Date().toISOString().split("T")[0],
      warranty_end_date: "",
    };
  };

  // Add a new row (either default or for a specific category)
  const handleAddGeneratedRow = (catId = null) => {
    const newRow = createEmptyRow(catId);
    setGeneratedRows((prev) => [...prev, newRow]);
  };

  // Duplicate a specific row (all details copied except serial number)
  const handleDuplicateRow = (idx) => {
    const source = generatedRows[idx];
    const newRow = {
      ...source,
      id: undefined,
      rowId: `pur-row-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
      serial_number: "",
    };
    setGeneratedRows((prev) => {
      const updated = [...prev];
      updated.splice(idx + 1, 0, newRow);
      return updated;
    });
    toast.success(`Row #${idx + 1} copy kar k new row add kar di gayi hai (Serial Number empty)!`);
  };

  // Delete a row
  const handleDeleteGeneratedRow = (globalIndex) => {
    setGeneratedRows((prev) => {
      if (prev.length <= 1) {
        toast.info("Cleared row details.");
        return [
          {
            ...prev[0],
            item_name: "",
            quantity: 1,
            unit_price: "",
            sale_price: "",
            sku: "",
            serial_number: "",
            part_number: "",
          },
        ];
      }
      return prev.filter((_, idx) => idx !== globalIndex);
    });
    toast.info("Row removed.");
  };

  // Change category of a row
  const handleRowCategoryChange = (idx, newCatId) => {
    const cat = categories.find((c) => String(c.id) === String(newCatId));
    if (!cat) return;
    const subs = cat.values && cat.values.length > 0
      ? cat.values
      : subcategories.filter((s) => s.category_id === cat.id);
    const defaultSub = subs[0] || { id: null, name: cat.name };

    setGeneratedRows((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        category_id: cat.id,
        category_name: cat.name,
        subcategory_id: defaultSub.id,
        value_name: defaultSub.name,
        serial_number: updated[idx].serial_number,
        part_number: updated[idx].part_number,
        sku: updated[idx].sku,
      };
      return updated;
    });
  };

  // Change subcategory of a row
  const handleRowSubcategoryChange = (idx, newSubId) => {
    const row = generatedRows[idx];
    const cat = categories.find((c) => String(c.id) === String(row.category_id));
    const subs = cat?.values && cat.values.length > 0
      ? cat.values
      : subcategories.filter((s) => s.category_id === row.category_id);
    const sub = subs.find((s) => String(s.id) === String(newSubId));
    if (!sub) return;

    setGeneratedRows((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        subcategory_id: sub.id,
        value_name: sub.name,
      };
      return updated;
    });
  };

  const handleRowChange = (index, field, value) => {
    setGeneratedRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // When an existing item suggestion is selected from autocomplete
  const handleSelectItemForRow = (idx, selectedItem) => {
    setGeneratedRows((prev) => {
      const updated = [...prev];
      const cur = updated[idx];
      updated[idx] = {
        ...cur,
        item_name: selectedItem.item_name,
        // If row has no subcategory or if selectedItem has one, update it
        subcategory_id: cur.subcategory_id || selectedItem.subcategory_id || cur.subcategory_id,
        value_name: cur.value_name || selectedItem.subcategory_name || cur.value_name,
        // Autofill unit_price if currently empty
        unit_price:
          (cur.unit_price === "" || cur.unit_price === undefined || cur.unit_price === null) &&
          selectedItem.unit_price !== undefined &&
          selectedItem.unit_price !== ""
            ? selectedItem.unit_price
            : cur.unit_price,
        // Autofill sale_price if currently empty
        sale_price:
          (cur.sale_price === "" || cur.sale_price === undefined || cur.sale_price === null) &&
          selectedItem.sale_price !== undefined &&
          selectedItem.sale_price !== ""
            ? selectedItem.sale_price
            : cur.sale_price,
        // Autofill sku if currently empty
        sku: !cur.sku && selectedItem.sku ? selectedItem.sku : cur.sku,
        // Autofill part_number if currently empty
        part_number: !cur.part_number && selectedItem.part_number ? selectedItem.part_number : cur.part_number,
      };
      return updated;
    });
  };

  // Copy Last Row: create a new row copying the last row's details (except serial number)
  const handleCopyLastRow = () => {
    if (generatedRows.length === 0) {
      handleAddGeneratedRow();
      return;
    }
    const lastRow = generatedRows[generatedRows.length - 1];
    const newRow = {
      ...lastRow,
      id: undefined,
      rowId: `pur-row-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
      serial_number: "", // Serial number must not be copied
    };
    setGeneratedRows((prev) => [...prev, newRow]);
    toast.success("Last row copy kar k new row add kar di gayi hai (Serial Number empty)! ");
  };

  // Download comprehensive purchase template with Category column included
  const handleDownloadTemplate = () => {
    try {
      const wsData = [
        [
          "Category*",
          "Subcategory / Spec",
          "Item Name*",
          "Count / Quantity*",
          "Purchase Price*",
          "Serial Number (Hardware Only)",
          "Part Number (Optional)",
          "SKU Number (Optional)",
          "Warranty Start",
          "Warranty End",
        ],
        [
          "Desktop",
          "Desktop",
          "HP EliteDesk 800 G4",
          "1",
          "35000",
          "SN-DESK-9901",
          "HP-ED-800",
          "SKU-DESK-001",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "LED",
          "LED",
          "HP 24f IPS LED Display",
          "1",
          "18000",
          "SN-LED-9901",
          "HP-24F-LED",
          "SKU-LED-001",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "NVME - GB",
          "512",
          "Kingston NV2 512GB NVMe M.2 SSD",
          "15",
          "8500",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 1095 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "RAM - GB",
          "16",
          "Samsung 16GB DDR4 3200MHz RAM",
          "20",
          "6200",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "Keyboard",
          "Keyboard",
          "Dell KB216 Wired Keyboard",
          "25",
          "2200",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "Mouse",
          "Mouse",
          "Logitech B100 USB Optical Mouse",
          "30",
          "1200",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        ],
        [
          "Mouse Pad",
          "Mouse Pad",
          "Standard Gaming Mouse Pad",
          "50",
          "400",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          "",
        ],
        [
          "Laptop Bag",
          "Laptop Bag",
          "Targus 15.6 Laptop Backpack",
          "10",
          "3500",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          "",
        ],
        [
          "Windows License",
          "Windows License",
          "Windows 11 Pro OEM Digital License",
          "10",
          "15000",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          "",
        ],
        [
          "Office License",
          "Office License",
          "Microsoft Office 2021 Home & Business",
          "10",
          "25000",
          "",
          "",
          "",
          purchaseDate || new Date().toISOString().split("T")[0],
          "",
        ],
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 18 },
        { wch: 22 },
        { wch: 35 },
        { wch: 16 },
        { wch: 16 },
        { wch: 26 },
        { wch: 22 },
        { wch: 20 },
        { wch: 16 },
        { wch: 16 },
      ];

      const refData = [
        ["Category Name", "Available Subcategories / Specifications"],
        ...categories.map((c) => {
          const subs = c.values && c.values.length > 0
            ? c.values.map((v) => v.name).join(", ")
            : subcategories.filter((s) => s.category_id === c.id).map((s) => s.name).join(", ");
          return [c.name, subs || c.name];
        }),
      ];
      const wsRef = XLSX.utils.aoa_to_sheet(refData);
      wsRef["!cols"] = [{ wch: 22 }, { wch: 45 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Purchase Items");
      XLSX.utils.book_append_sheet(wb, wsRef, "Category Reference");
      XLSX.writeFile(wb, "purchase_items_template.xlsx");
      toast.success("Purchase Items Template downloaded!");
    } catch (err) {
      console.error("Download template error:", err);
      toast.error("Failed to generate download template.");
    }
  };

  // Bulk Upload sheet parser: Handles Category column, multi-column category sheets, and legacy formats
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log("%c[PURCHASE SHEET UPLOAD] Selected file:", "color: #2563EB; font-weight: bold; font-size: 13px;", {
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      type: file.type,
      lastModified: new Date(file.lastModified).toISOString(),
    });

    const reader = new FileReader();
    reader.onerror = (readErr) => {
      console.error("%c[PURCHASE SHEET UPLOAD ERROR] FileReader error:", "color: #DC2626; font-weight: bold;", readErr);
      toast.error(`FileReader error: ${readErr.message || "Could not read file"}`);
    };
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        console.log("%c[PURCHASE SHEET UPLOAD] Workbook parsed successfully:", "color: #059669; font-weight: bold;", {
          sheetNames: workbook.SheetNames,
        });

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        console.log("%c[PURCHASE SHEET UPLOAD] Extracted raw rows count:", "color: #2563EB; font-weight: bold;", rawJson.length);
        console.log("%c[PURCHASE SHEET UPLOAD] Raw rows data:", "color: #2563EB;", rawJson);

        if (!rawJson || rawJson.length === 0) {
          console.warn("[PURCHASE SHEET UPLOAD] The uploaded sheet appears to be empty.");
          toast.warning("The uploaded sheet is empty.");
          return;
        }

        const formatDate = (val) => {
          if (!val) return "";
          if (val instanceof Date && !isNaN(val)) {
            return val.toISOString().split("T")[0];
          }
          if (typeof val === "number") {
            const dateObj = new Date((val - (25567 + 2)) * 86400 * 1000);
            if (!isNaN(dateObj)) return dateObj.toISOString().split("T")[0];
          }
          const str = String(val).trim();
          const d = new Date(str);
          if (!isNaN(d.getTime())) {
            return d.toISOString().split("T")[0];
          }
          return str;
        };

        // Auto-detect bill / invoice number from sheet if present
        const rowWithBill = rawJson.find((row) => {
          return Object.keys(row).some((k) => {
            const clean = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
              (clean.includes("billno") ||
                clean.includes("billnumber") ||
                clean.includes("invoiceno") ||
                clean.includes("invoicenumber") ||
                clean === "bill" ||
                clean === "invoice") &&
              String(row[k]).trim() !== ""
            );
          });
        });
        if (rowWithBill) {
          const billKey = Object.keys(rowWithBill).find((k) => {
            const clean = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
              clean.includes("billno") ||
              clean.includes("billnumber") ||
              clean.includes("invoiceno") ||
              clean.includes("invoicenumber") ||
              clean === "bill" ||
              clean === "invoice"
            );
          });
          if (billKey && rowWithBill[billKey]) {
            const detectedBill = String(rowWithBill[billKey]).trim();
            console.log("[PURCHASE SHEET UPLOAD] Auto-detected bill number from sheet:", detectedBill);
            setBillNumber(detectedBill);
          }
        }

        // Helper to match category by string
        const findCategoryMatch = (str) => {
          if (!str) return null;
          const clean = String(str).toLowerCase().replace(/[^a-z0-9]/g, "");
          return (
            categories.find((c) => c.name.toLowerCase().replace(/[^a-z0-9]/g, "") === clean) ||
            categories.find((c) => {
              const cClean = c.name.toLowerCase().replace(/[^a-z0-9]/g, "");
              return cClean.includes(clean) || clean.includes(cClean);
            }) ||
            null
          );
        };

        // Helper to match subcategory by string within a category
        const findSubcategoryMatch = (catId, str) => {
          const subs = subcategories.filter((s) => s.category_id === catId);
          if (!str) return subs[0] || null;
          const clean = String(str).toLowerCase().trim();
          return (
            subs.find((s) => s.name.toLowerCase().trim() === clean) ||
            subs.find((s) => s.name.toLowerCase().includes(clean) || clean.includes(s.name.toLowerCase())) ||
            subs[0] ||
            null
          );
        };

        // Case 1: Legacy PC format sheet (contains Desktop or LED in headers)
        const isLegacyPcFormat = rawJson.some((row) => {
          const keys = Object.keys(row).map((k) => k.toLowerCase().replace(/[^a-z0-9]/g, ""));
          return (
            keys.some((k) => k.includes("desktopitem") || k.includes("desktopserial") || k.includes("desktopprice")) &&
            keys.some((k) => k.includes("leditem") || k.includes("ledserial") || k.includes("ledprice"))
          );
        });

        if (isLegacyPcFormat) {
          console.log("[PURCHASE SHEET UPLOAD] Detected sheet format: Case 1 (Legacy PC - Desktop + LED Columns)");
          const deskCat = categories.find((c) => c.name.toLowerCase().includes("desktop")) || categories[0];
          const deskSub = subcategories.find((s) => s.category_id === deskCat?.id) || { id: null, name: "Desktop" };
          const ledCat = categories.find((c) => c.name.toLowerCase().includes("led")) || categories[0];
          const ledSub = subcategories.find((s) => s.category_id === ledCat?.id) || { id: null, name: "LED" };

          const parsedRows = [];
          rawJson.forEach((row, idx) => {
            const normalized = {};
            Object.keys(row).forEach((k) => {
              const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, "");
              normalized[cleanKey] = row[k];
            });

            const wStart = formatDate(normalized["warrantystart"] || normalized["warrantystartdate"] || purchaseDate);
            const wEnd = formatDate(normalized["warrantyend"] || normalized["warrantyenddate"] || "");

            // Desktop item
            const dName = normalized["desktopitemname"] || normalized["desktopname"] || normalized["desktop"] || "";
            const dPart = normalized["desktoppartnumber"] || normalized["desktoppartno"] || normalized["desktoppn"] || normalized["desktoppart"] || "";
            const dSku = normalized["desktopskunumber"] || normalized["desktopsku"] || normalized["desktopskuno"] || "";
            const dSn = normalized["desktopserialnumber"] || normalized["desktopserialno"] || normalized["desktopsn"] || normalized["desktopserial"] || "";
            const dPrice =
              normalized["desktopprice"] ||
              normalized["desktopcost"] ||
              normalized["desktopcoat"] ||
              normalized["desktopunitcost"] ||
              normalized["desktopunitcoat"] ||
              normalized["desktoprate"] ||
              "";

            if (dName || dSn || dPart || dSku) {
              parsedRows.push({
                rowId: `pur-row-desk-${Date.now()}-${idx}-${Math.random()}`,
                category_id: deskCat?.id || null,
                category_name: deskCat?.name || "Desktop",
                subcategory_id: deskSub.id,
                value_name: deskSub.name,
                item_name: String(dName).trim() || "Desktop PC",
                part_number: String(dPart).trim(),
                sku: String(dSku).trim(),
                serial_number: String(dSn).trim(),
                unit_price: dPrice !== "" && !isNaN(dPrice) ? String(dPrice) : "",
                warranty_start_date: wStart || purchaseDate || "",
                warranty_end_date: wEnd || "",
              });
            }

            // LED item
            const lName = normalized["leditemname"] || normalized["ledname"] || normalized["led"] || "";
            const lPart = normalized["ledpartnumber"] || normalized["ledpartno"] || normalized["ledpn"] || normalized["ledpart"] || "";
            const lSku = normalized["ledskunumber"] || normalized["ledsku"] || normalized["ledskuno"] || "";
            const lSn = normalized["ledserialnumber"] || normalized["ledserialno"] || normalized["ledsn"] || normalized["ledserial"] || "";
            const lPrice =
              normalized["ledprice"] ||
              normalized["ledcost"] ||
              normalized["ledcoat"] ||
              normalized["ledunitcost"] ||
              normalized["ledunitcoat"] ||
              normalized["ledrate"] ||
              "";

            if (lName || lSn || lPart || lSku) {
              parsedRows.push({
                rowId: `pur-row-led-${Date.now()}-${idx}-${Math.random()}`,
                category_id: ledCat?.id || null,
                category_name: ledCat?.name || "LED",
                subcategory_id: ledSub.id,
                value_name: ledSub.name,
                item_name: String(lName).trim() || "LED Display",
                part_number: String(lPart).trim(),
                sku: String(lSku).trim(),
                serial_number: String(lSn).trim(),
                unit_price: lPrice !== "" && !isNaN(lPrice) ? String(lPrice) : "",
                warranty_start_date: wStart || purchaseDate || "",
                warranty_end_date: wEnd || "",
              });
            }
          });

          if (parsedRows.length > 0) {
            console.log("[PURCHASE SHEET UPLOAD] Parsed rows from Legacy PC format:", parsedRows);
            setGeneratedRows(parsedRows);
            toast.success(`Loaded ${parsedRows.length} item(s) (Desktop & LED) from sheet!`);
            return;
          }
        }

        // Case 2: Multi-column category sheet format (columns named NVME - GB, RAM - GB, Mouse, etc.)
        const firstRowKeys = Object.keys(rawJson[0] || {});
        const categoryColumnMatches = firstRowKeys.filter((k) => findCategoryMatch(k));
        const hasStandardItemOrCategoryCol = firstRowKeys.some((k) => {
          const clean = k.toLowerCase().replace(/[^a-z0-9]/g, "");
          return clean === "category" || clean === "categoryname" || clean === "itemname" || clean === "item" || clean === "product";
        });

        if (!hasStandardItemOrCategoryCol && categoryColumnMatches.length >= 3) {
          console.log("[PURCHASE SHEET UPLOAD] Detected sheet format: Case 2 (Multi-column categories)", categoryColumnMatches);
          const parsedRows = [];
          rawJson.forEach((row, rowIdx) => {
            categoryColumnMatches.forEach((colKey) => {
              const cellVal = String(row[colKey] || "").trim();
              if (!cellVal) return;
              const cat = findCategoryMatch(colKey);
              if (!cat) return;
              const sub = findSubcategoryMatch(cat.id, cellVal);
              const subName = sub ? sub.name : cellVal;
              parsedRows.push({
                rowId: `pur-row-col-${Date.now()}-${rowIdx}-${Math.random()}`,
                category_id: cat.id,
                category_name: cat.name,
                subcategory_id: sub ? sub.id : null,
                value_name: subName,
                item_name: `${cat.name} ${cellVal !== cat.name ? cellVal : ""}`.trim(),
                part_number: "",
                sku: "",
                serial_number: "",
                unit_price: "",
                warranty_start_date: purchaseDate || new Date().toISOString().split("T")[0],
                warranty_end_date: "",
              });
            });
          });

          if (parsedRows.length > 0) {
            console.log("[PURCHASE SHEET UPLOAD] Parsed rows from Multi-column format:", parsedRows);
            setGeneratedRows(parsedRows);
            toast.success(`Loaded ${parsedRows.length} item(s) from category-columns sheet into the table!`);
            return;
          }
        }

        // Case 3: Standard Purchase Items sheet with Category column or standard row items
        console.log("[PURCHASE SHEET UPLOAD] Parsing as Case 3 (Standard Item Rows)");
        const parsedRows = rawJson.map((row, idx) => {
          if (!row || typeof row !== "object") return null;

          // Check if every value in row is empty or whitespace
          const hasAnyCell = Object.values(row).some((val) => {
            if (val === null || val === undefined) return false;
            const s = String(val).trim();
            return s !== "" && s !== "undefined" && s !== "null";
          });
          if (!hasAnyCell) return null;

          const normalized = {};
          Object.keys(row).forEach((k) => {
            const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            normalized[cleanKey] = row[k];
          });

          // Robust identifier extractors to support downloaded templates and custom sheets
          const getSerialVal = () => {
            const keys = Object.keys(normalized);
            // 1. Exact match for dedicated serial headers
            for (const k of keys) {
              if (["serialnumberhardwareonly", "serialnumber", "serialno", "serialnum", "serial", "hardwareserial", "sn"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            // 2. Substring match for serial headers (ignoring composite identifier/part/sku/name/desc/model/type columns)
            for (const k of keys) {
              if (k.includes("identifier") || k.includes("sku") || k.includes("part") || k.includes("name") || k.includes("desc") || k.includes("model") || k.includes("type")) continue;
              if (k.includes("serial") || k.endsWith("sn") || k === "sn") {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          const getPartVal = () => {
            const keys = Object.keys(normalized);
            // 1. Exact match
            for (const k of keys) {
              if (["partnumber", "partno", "partnum", "pn", "part", "mpn"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            // 2. Substring match
            for (const k of keys) {
              if (k.includes("serial") || k.includes("sku") || k.includes("identifier") || k.includes("depart") || k.includes("party")) continue;
              if (k.includes("partnumber") || k.includes("partno") || k.includes("partnum") || k.startsWith("part") || k.endsWith("part") || k === "pn") {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          const getSkuVal = () => {
            const keys = Object.keys(normalized);
            // 1. Exact match
            for (const k of keys) {
              if (["skunumber", "skuno", "sku"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            // 2. Substring match
            for (const k of keys) {
              if (k.includes("serial") || k.includes("part") || k.includes("identifier")) continue;
              if (k.includes("sku")) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          let catRaw = String(
            normalized["category"] ||
            normalized["categoryname"] ||
            normalized["cat"] ||
            normalized["itemcategory"] ||
            normalized["type"] ||
            ""
          ).trim();
          if (!catRaw) {
            const catKey = Object.keys(normalized).find(
              (k) => (k.startsWith("cat") || k.includes("category")) && !k.includes("subcat")
            );
            if (catKey) catRaw = String(normalized[catKey] || "").trim();
          }

          let subRaw = String(
            normalized["subcategoryspec"] ||
            normalized["subcategory"] ||
            normalized["subcat"] ||
            normalized["spec"] ||
            normalized["specification"] ||
            normalized["size"] ||
            normalized["gb"] ||
            ""
          ).trim();
          if (!subRaw) {
            const subKey = Object.keys(normalized).find(
              (k) => (k.includes("subcat") || k.includes("spec")) && !k.includes("item") && !k.includes("part")
            );
            if (subKey) subRaw = String(normalized[subKey] || "").trim();
          }

          let itemName = String(
            normalized["itemname"] ||
            normalized["itemdescription"] ||
            normalized["description"] ||
            normalized["productname"] ||
            normalized["product"] ||
            normalized["item"] ||
            normalized["name"] ||
            ""
          ).trim();

          const partNumber = getPartVal();
          const skuNumber = getSkuVal();
          const serialNumber = getSerialVal();

          // Extract Quantity / Count
          const rawQuantity =
            normalized["count"] ||
            normalized["quantity"] ||
            normalized["qty"] ||
            normalized["countquantity"] ||
            normalized["itemcount"] ||
            normalized["totalqty"] ||
            normalized["totalquantity"] ||
            normalized["units"] ||
            normalized["pieces"] ||
            normalized["pcs"] ||
            1;

          const parsedQty = Math.max(1, parseInt(rawQuantity) || 1);

          // Clean numeric helper (strips commas, currency words, whitespace)
          const cleanNumeric = (val) => {
            if (val === null || val === undefined) return "";
            if (typeof val === "number" && !isNaN(val)) return String(val);
            const str = String(val).trim().replace(/,/g, "");
            const match = str.match(/[-+]?[0-9]*\.?[0-9]+/);
            return match ? match[0] : "";
          };

          // 1. Detect Unit Cost / Unit Coat (handles 'unit coat', 'unit cost', 'purchase price', 'rate', etc.)
          let rawUnitPrice = "";
          const unitPriceDirectKeys = [
            "unitcoat",
            "unitcoatrs",
            "unitcost",
            "unitcostrs",
            "unitprice",
            "unitpricers",
            "purchaseprice",
            "purchasepricers",
            "purchasecost",
            "purchasecostrs",
            "purchasecoat",
            "purchasecoatrs",
            "purprice",
            "purcost",
            "purcoat",
            "rate",
            "unitrate",
            "rateperunit",
            "costperunit",
            "coatperunit",
            "cost",
            "coat",
            "price",
          ];

          for (const k of unitPriceDirectKeys) {
            if (normalized[k] !== undefined && normalized[k] !== null && String(normalized[k]).trim() !== "") {
              rawUnitPrice = normalized[k];
              break;
            }
          }

          // Fuzzy search across all column keys if direct key not matched
          if (!rawUnitPrice) {
            const allKeys = Object.keys(normalized);
            const foundKey = allKeys.find((k) => {
              if (k.includes("sale") || k.includes("selling")) return false;
              if (k.includes("total") || k.includes("amount")) return false;
              return (
                k.includes("coat") ||
                k.includes("unitcost") ||
                k.includes("unitprice") ||
                k.includes("purchaseprice") ||
                k.includes("purchasecost") ||
                k.includes("rate") ||
                k.includes("cost") ||
                k.includes("price")
              );
            });
            if (foundKey && String(normalized[foundKey]).trim() !== "") {
              rawUnitPrice = normalized[foundKey];
            }
          }

          // 2. Detect Total Price / Amount (in case total price is mentioned without unit coat/cost)
          let rawTotalPrice = "";
          const totalDirectKeys = [
            "totalprice",
            "totalpricers",
            "totalcost",
            "totalcostrs",
            "totalcoat",
            "totalamount",
            "totalamountrs",
            "nettotal",
            "netamount",
            "total",
            "totalrs",
            "amount",
            "amountrs",
            "linetotal",
            "lineamount",
          ];

          for (const k of totalDirectKeys) {
            if (normalized[k] !== undefined && normalized[k] !== null && String(normalized[k]).trim() !== "") {
              rawTotalPrice = normalized[k];
              break;
            }
          }

          if (!rawTotalPrice) {
            const allKeys = Object.keys(normalized);
            const foundTotalKey = allKeys.find((k) => {
              if (k.includes("subtotal") || k.includes("count") || k.includes("qty") || k.includes("quantity")) return false;
              return k.includes("total") || k.includes("amount");
            });
            if (foundTotalKey && String(normalized[foundTotalKey]).trim() !== "") {
              rawTotalPrice = normalized[foundTotalKey];
            }
          }

          // Calculate final unit price
          let finalUnitPrice = cleanNumeric(rawUnitPrice);
          const finalTotalPrice = cleanNumeric(rawTotalPrice);

          // If unit coat / unit cost was not mentioned, but total price WAS mentioned:
          if ((!finalUnitPrice || parseFloat(finalUnitPrice) === 0) && finalTotalPrice && parseFloat(finalTotalPrice) > 0) {
            finalUnitPrice = (parseFloat(finalTotalPrice) / parsedQty).toFixed(2);
          }

          // If all essential item fields are empty, skip row completely!
          if (!catRaw && !itemName && !serialNumber && !partNumber && !skuNumber && !finalUnitPrice) {
            return null;
          }

          // Match category
          let matchedCat = findCategoryMatch(catRaw);
          if (!matchedCat && itemName) {
            const lowerName = itemName.toLowerCase();
            if (lowerName.includes("nvme") || lowerName.includes("m.2") || lowerName.includes("ssd")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("nvme"));
            } else if (lowerName.includes("ram") || lowerName.includes("ddr")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("ram"));
            } else if (lowerName.includes("mouse pad") || lowerName.includes("mousepad")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("mouse pad"));
            } else if (lowerName.includes("mouse")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("mouse"));
            } else if (lowerName.includes("keyboard")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("keyboard"));
            } else if (lowerName.includes("bag") || lowerName.includes("backpack")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("bag"));
            } else if (lowerName.includes("windows")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("windows"));
            } else if (lowerName.includes("office")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("office"));
            } else if (lowerName.includes("led") || lowerName.includes("monitor") || lowerName.includes("display")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("led"));
            } else if (lowerName.includes("desktop") || lowerName.includes("cpu") || lowerName.includes("optiplex") || lowerName.includes("elitedesk")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("desktop"));
            } else if (lowerName.includes("laptop") || lowerName.includes("latitude") || lowerName.includes("thinkpad")) {
              matchedCat = categories.find((c) => c.name.toLowerCase().includes("laptop"));
            }
          }
          if (!matchedCat) {
            matchedCat = categories[0];
          }

          const matchedSub = findSubcategoryMatch(matchedCat.id, subRaw || itemName);

          // Final item name: only auto-generate from category if category was actually entered in the sheet
          const finalItemName = itemName || (catRaw && matchedCat ? `${matchedCat.name} ${matchedSub ? matchedSub.name : ""}`.trim() : "");

          if (!finalItemName && !serialNumber && !partNumber && !skuNumber && !finalUnitPrice) {
            return null;
          }

          const wStart = formatDate(
            normalized["warrantystart"] ||
            normalized["warrantystartdate"] ||
            normalized["startdate"] ||
            purchaseDate
          );

          const wEnd = formatDate(
            normalized["warrantyend"] ||
            normalized["warrantyenddate"] ||
            normalized["enddate"] ||
            ""
          );

          const rawSalePrice =
            normalized["saleprice"] ||
            normalized["salepriceoptional"] ||
            normalized["salesprice"] ||
            normalized["sellingprice"] ||
            "";

          const isBulk = isBulkCategory(matchedCat ? matchedCat.id : null, matchedCat ? matchedCat.name : finalItemName);

          return {
            rowId: `pur-row-upload-${Date.now()}-${idx}-${Math.random()}`,
            category_id: matchedCat ? matchedCat.id : null,
            category_name: matchedCat ? matchedCat.name : "",
            subcategory_id: matchedSub ? matchedSub.id : null,
            value_name: matchedSub ? matchedSub.name : matchedCat?.name || "Item",
            item_name: finalItemName,
            quantity: isBulk ? parsedQty : 1,
            unit_price: finalUnitPrice !== "" && !isNaN(finalUnitPrice) ? String(finalUnitPrice) : "",
            sale_price: rawSalePrice !== "" && !isNaN(rawSalePrice) ? String(rawSalePrice) : "",
            part_number: String(partNumber).trim(),
            sku: String(skuNumber).trim(),
            serial_number: String(serialNumber).trim(),
            warranty_start_date: wStart || purchaseDate || new Date().toISOString().split("T")[0],
            warranty_end_date: wEnd || "",
          };
        });

        const validRows = parsedRows.filter(
          (r) => r !== null && (r.item_name || r.serial_number || r.part_number || r.sku || r.unit_price)
        );

        console.log("%c[PURCHASE SHEET UPLOAD] Parsed result summary:", "color: #059669; font-weight: bold; font-size: 13px;", {
          rawRowsCount: rawJson.length,
          parsedRowsCount: parsedRows.filter(Boolean).length,
          validRowsCount: validRows.length,
          validRows: validRows,
        });

        if (validRows.length === 0) {
          console.warn("%c[PURCHASE SHEET UPLOAD] 0 valid rows found. Sheet columns/headers were:", "color: #D97706; font-weight: bold;", {
            firstRowHeaders: Object.keys(rawJson[0] || {}),
            rawRows: rawJson,
          });
          toast.warning("No valid items found in the sheet. Check browser console (F12) for details.");
          return;
        }

        setGeneratedRows(validRows);
        toast.success(`Loaded ${validRows.length} item(s) from sheet into the table!`);
      } catch (err) {
        console.error("%c[PURCHASE SHEET UPLOAD ERROR]:", "color: #DC2626; font-weight: bold; font-size: 14px;", err);
        console.error("Stack trace:", err.stack);
        toast.error(`Sheet parse error: ${err.message || "Check browser console"}`);
      } finally {
        if (e.target) e.target.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Submit Vendor Purchase Record
  const handleRecordPurchase = async (e) => {
    e.preventDefault();

    if (!selectedVendorId) {
      toast.warning("Please select a Vendor!");
      return;
    }
    if (!purchaseDate) {
      toast.warning("Please specify Purchase Date!");
      return;
    }

    try {
      setSubmitting(true);

      // Validation: Item Name and Category on every row
      if (generatedRows.length === 0) {
        toast.warning("No item rows generated to record. Please add at least one item.");
        setSubmitting(false);
        return;
      }

      for (let i = 0; i < generatedRows.length; i++) {
        const r = generatedRows[i];
        if (!r.item_name || !r.item_name.trim()) {
          toast.warning(`Row #${i + 1}: Item Name is required.`);
          setSubmitting(false);
          return;
        }
        if (!r.category_id) {
          toast.warning(`Row #${i + 1}: Category is required.`);
          setSubmitting(false);
          return;
        }
        if (!r.subcategory_id) {
          const matchingSub = subcategories.find(
            (s) => s.category_id === r.category_id && s.name.toLowerCase() === (r.value_name || "").toLowerCase()
          ) || subcategories.find((s) => s.category_id === r.category_id);
          if (matchingSub) {
            r.subcategory_id = matchingSub.id;
          }
        }
        if (r.warranty_start_date && r.warranty_end_date && new Date(r.warranty_end_date) < new Date(r.warranty_start_date)) {
          toast.warning(`Row #${i + 1} (${r.item_name}): Warranty End Date cannot be earlier than Warranty Start Date.`);
          setSubmitting(false);
          return;
        }
      }


      const payload = {
        vendor_id: parseInt(selectedVendorId),
        purchase_date: purchaseDate,
        bill_number: billNumber.trim() || undefined,
        notes: notes || null,
        items: generatedRows.map((r) => {
          const isBulk = isBulkCategory(r.category_id, r.category_name);
          const itemPrice =
            r.unit_price !== "" && r.unit_price !== null && !isNaN(r.unit_price)
              ? parseFloat(r.unit_price)
              : 0;
          const salePrice =
            r.sale_price !== "" && r.sale_price !== null && !isNaN(r.sale_price)
              ? parseFloat(r.sale_price)
              : null;
          const qty = isBulk ? Math.max(1, parseInt(r.quantity) || 1) : 1;

          return {
            id: r.id || undefined,
            category_id: r.category_id,
            subcategory_id: r.subcategory_id,
            item_name: r.item_name.trim(),
            quantity: qty,
            min_stock_alert: r.min_stock_alert !== undefined && r.min_stock_alert !== null && !isNaN(r.min_stock_alert) ? parseInt(r.min_stock_alert) : 5,
            unit_price: itemPrice,
            price: itemPrice,
            sale_price: salePrice,
            total_price: itemPrice * qty,
            serial_number: r.serial_number && r.serial_number.trim() ? r.serial_number.trim() : null,
            part_number: r.part_number && r.part_number.trim() ? r.part_number.trim() : null,
            sku: r.sku && r.sku.trim() ? r.sku.trim() : null,
            warranty_start_date: r.warranty_start_date || null,
            warranty_end_date: r.warranty_end_date || null,
          };
        }),
      };

      if (editingPurchase) {
        console.log("%c[PURCHASE UPDATE] Submitting update payload to API:", "color: #2563EB; font-weight: bold;", payload);
        const res = await purchaseApi.update(editingPurchase.id || editingPurchase.originalBillNumber, payload);
        console.log("%c[PURCHASE UPDATE] Purchase successfully updated:", "color: #2563EB; font-weight: bold;", res);

        toast.success(res?.message || `Purchase Bill #${payload.bill_number || editingPurchase.originalBillNumber} updated successfully!`);
        handleResetForm();
        setSearchParams({ tab: "record" });
        await fetchData(true);
        return;
      }

      console.log("%c[PURCHASE RECORD] Submitting payload to API:", "color: #059669; font-weight: bold;", payload);
      const res = await purchaseApi.create(payload);
      console.log("%c[PURCHASE RECORD] Purchase successfully recorded:", "color: #059669; font-weight: bold;", res);

      const assignedBill =
        res?.bill_number ||
        (Array.isArray(res?.data) && res.data[0]?.bill_number) ||
        res?.data?.bill_number ||
        billNumber.trim();
      const billNotice = assignedBill ? ` under Bill #${assignedBill}` : "";

      toast.success(`Successfully recorded purchase with ${generatedRows.length} item(s)${billNotice}!`);
      handleResetForm();
      setSearchParams({ tab: "record" });
      await fetchData(true);
      return;
    } catch (err) {
      console.error("%c[PURCHASE RECORD SUBMIT ERROR]:", "color: #DC2626; font-weight: bold; font-size: 14px;", err);
      if (err.response) {
        console.error("%c[PURCHASE RECORD BACKEND ERROR DATA]:", "color: #DC2626; font-weight: bold;", {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
        });
      }
      const backendError = err.response?.data?.error || err.response?.data?.message;
      const errMsg = backendError || err.message || "Failed to record purchase";
      toast.error(`Purchase error: ${errMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals for bottom summary
  const totalItemCount = generatedRows.length;
  const totalPhysicalUnits = generatedRows.reduce((sum, r) => {
    const isBulk = isBulkCategory(r.category_id, r.category_name);
    return sum + (isBulk ? Math.max(1, parseInt(r.quantity) || 1) : 1);
  }, 0);
  const totalPrice = generatedRows.reduce((sum, r) => {
    const val = parseFloat(r.unit_price);
    const qty = Math.max(1, parseInt(r.quantity) || 1);
    return sum + (!isNaN(val) && val > 0 ? val * qty : 0);
  }, 0);

  // Current selected vendor name for display
  const currentVendor = vendors.find((v) => String(v.id) === String(selectedVendorId));

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "—";
    try {
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const parts = dateStr.slice(0, 10).split("-");
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(dateStr);
    }
  };

  const totalHistoryExpenditure = useMemo(() => {
    return groupedBills.reduce((sum, b) => sum + (parseFloat(b.total_price) || 0), 0);
  }, [groupedBills]);

  const totalHistoryUnits = useMemo(() => {
    return groupedBills.reduce((sum, b) => sum + (b.total_quantity || 0), 0);
  }, [groupedBills]);

  return (
    <div className="fade-in">
      {/* Top Header & Page Navigation Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--navy)" }}>
            {activeTab === "record"
              ? "Record Vendor Purchase"
              : activeTab === "history"
              ? "Purchase History"
              : "Purchase Returns"}
          </h2>
        </div>

        {/* View Mode Toggle: Record Purchase vs Purchase History vs Returns */}
        <div
          style={{
            display: "flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={() => handleTabChange("record")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "record" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "record" ? "var(--primary-light)" : "#fff",
              color: activeTab === "record" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "record" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiPlus size={16} />
            Record Purchase
          </button>

          <button
            type="button"
            onClick={() => handleTabChange("history")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "history" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "history" ? "var(--primary-light)" : "#fff",
              color: activeTab === "history" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "history" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiClock size={16} />
            Purchase History
          </button>

          <button
            type="button"
            onClick={() => handleTabChange("returns")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "returns" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "returns" ? "var(--primary-light)" : "#fff",
              color: activeTab === "returns" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "returns" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiRotateCcw size={16} />
            Purchase Returns
          </button>
        </div>
      </div>

      {/* Top Dashboard-Style Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* Card 1: Total Purchase Amount */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view purchase history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Purchase Amount
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#DCFCE7", color: "#15803D" }}>
              <FiShoppingCart size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#15803D",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Rs. ${totalHistoryExpenditure.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          >
            Rs. {totalHistoryExpenditure.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#15803D" }} />
            <span>{totalHistoryUnits.toLocaleString()} items purchased across {groupedBills.length} bills</span>
          </div>
        </div>

        {/* Card 2: Total Purchased Items */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view purchase history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Purchased Items
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "var(--primary-light, #E6F4EA)", color: "var(--primary, #064E3B)" }}>
              <FiBox size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "var(--navy)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {totalHistoryUnits.toLocaleString()} <span style={{ fontSize: "1rem", fontWeight: 600, color: "var(--slate-500)" }}>Units</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--primary, #064E3B)" }} />
            <span>Total asset inventory units acquired</span>
          </div>
        </div>

        {/* Card 3: Total Purchase Bills */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view purchase history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Purchase Bills
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#EFF6FF", color: "#2563EB" }}>
              <FiFileText size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#2563EB",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {groupedBills.length} <span style={{ fontSize: "1rem", fontWeight: 600, color: "var(--slate-500)" }}>Bills</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2563EB" }} />
            <span>Total vendor invoices recorded</span>
          </div>
        </div>
      </div>

      {activeTab === "record" ? (
        <form onSubmit={handleRecordPurchase}>
          {editingPurchase && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#EFF6FF",
                border: "1.5px solid #3B82F6",
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 20,
                gap: 14,
                flexWrap: "wrap",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    backgroundColor: "#DBEAFE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1D4ED8",
                    flexShrink: 0,
                  }}
                >
                  <FiEdit size={22} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1rem", color: "#1E3A8A" }}>
                    Editing Purchase Bill: #{editingPurchase.originalBillNumber}
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#2563EB", marginTop: 2 }}>
                    You can add new item rows, delete items, modify prices, or change vendor & bill details.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "1.5px solid #93C5FD",
                  backgroundColor: "#fff",
                  color: "#1D4ED8",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <FiX size={16} /> Cancel Edit
              </button>
            </div>
          )}

          {/* Step 1: Vendor, Customer & Purchase Date Information */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              padding: "22px 24px",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiTruck size={20} />
              </div>
              <div>
                <h3 style={{ fontSize: "1.12rem", fontWeight: 800, color: "var(--navy)" }}>
                  1. Vendor & Purchase Date
                </h3>

              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 18,
              }}
            >
              {/* Select Vendor */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Select Vendor *
                </label>
                <SearchableVendorSelect
                  vendors={vendors}
                  selectedVendorId={selectedVendorId}
                  onSelect={(id) => setSelectedVendorId(id)}
                  placeholder="-- Choose or search vendor supplier --"
                />
              </div>

              {/* Purchase Date */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Purchase Date *
                </label>
                <input
                  type="date"
                  required
                  value={purchaseDate}
                  onChange={(e) => {
                    setPurchaseDate(e.target.value);
                    // Also update warranty start date on generated rows if empty
                    setGeneratedRows((prev) =>
                      prev.map((r) => ({
                        ...r,
                        warranty_start_date: r.warranty_start_date || e.target.value,
                      }))
                    );
                  }}
                  style={{
                    width: "100%",
                    height: 42,
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    fontWeight: 600,
                    color: "var(--navy)",
                    fontSize: "0.86rem",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Bill / Invoice Number */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)" }}>
                    Bill / Invoice #
                  </label>
                  <span style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 500 }}>
                    (Shared for all items)
                  </span>
                </div>
                <div style={{ position: "relative" }}>
                  <FiFileText
                    size={16}
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--slate-400)",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="e.g. BILL-2026-001 (auto-generated if empty)"
                    value={billNumber}
                    onChange={(e) => setBillNumber(e.target.value)}
                    style={{
                      width: "100%",
                      height: 42,
                      padding: "9px 14px 9px 36px",
                      borderRadius: 8,
                      border: "1.5px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      fontWeight: 600,
                      color: "var(--navy)",
                      fontSize: "0.86rem",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Item Selection Mode Toggle */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              padding: "20px 24px",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div>
                <h3 style={{ fontSize: "1.12rem", fontWeight: 800, color: "var(--navy)" }}>
                  2. Configure Items to Purchase
                </h3>

              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {/* Download Items Template Button */}
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #C7D2FE",
                    backgroundColor: "#EEF2FF",
                    color: "#4338CA",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "all 0.15s ease",
                  }}
                  title="Download Excel spreadsheet template with Category column included"
                >
                  <FiDownload size={15} />
                  Download Excel Template (with Category)
                </button>

                {/* Bulk Upload Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: "var(--primary)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(6,78,59,0.2)",
                    transition: "all 0.15s ease",
                  }}
                  title="Upload an Excel or CSV spreadsheet"
                >
                  <FiUpload size={15} />
                  Bulk Upload Sheet
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xlsx, .xls, .csv"
                  style={{ display: "none" }}
                />
              </div>
            </div>

            <div>
              {/* Quick Add By Category Bar */}
              <div
                style={{
                  backgroundColor: "var(--slate-50)",
                  padding: "12px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-200)",
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                  {/* Row 1: Hardware Assets (Serialized - 1 unit per row) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "#1D4ED8", textTransform: "uppercase", backgroundColor: "#EFF6FF", padding: "2px 6px", borderRadius: 4 }}>
                      Hardware (Serialized):
                    </span>
                    {categories
                      .filter((c) => c.is_serialized === 1 || c.category_type === "serialized")
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleAddGeneratedRow(c.id)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            backgroundColor: "#fff",
                            color: "#1E40AF",
                            border: "1px solid #BFDBFE",
                            padding: "4px 8px",
                            borderRadius: 6,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                          title={`Add a new ${c.name} row (1 Unit / Row)`}
                        >
                          <FiPlus size={11} /> {c.name}
                        </button>
                      ))}
                  </div>

                  {/* Row 2: Bulk Accessories & Software (Count & Price) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "#047857", textTransform: "uppercase", backgroundColor: "#ECFDF5", padding: "2px 6px", borderRadius: 4 }}>
                      Bulk Accessories & Licenses:
                    </span>
                    {categories
                      .filter((c) => c.is_serialized === 0 || c.category_type === "bulk")
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleAddGeneratedRow(c.id)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            backgroundColor: "#fff",
                            color: "#065F46",
                            border: "1px solid #A7F3D0",
                            padding: "4px 8px",
                            borderRadius: 6,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                          title={`Add a new ${c.name} row (Specify Count & Prices)`}
                        >
                          <FiPlus size={11} /> {c.name}
                        </button>
                      ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => handleAddGeneratedRow()}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      border: "none",
                      padding: "7px 14px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    <FiPlus size={13} /> + Add Item Row
                  </button>
                  {generatedRows.length > 0 && (
                    <button
                      type="button"
                      onClick={handleCopyLastRow}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        backgroundColor: "#fff",
                        color: "var(--primary)",
                        border: "1px solid var(--primary-light)",
                        padding: "7px 14px",
                        borderRadius: 6,
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      }}
                      title="Last row ki copy bana kar new row add karein (Serial Number empty rahega)"
                    >
                      <FiCopy size={13} /> Copy Row
                    </button>
                  )}
                </div>
              </div>

              {/* Items Table Card */}
              <div
                style={{
                  marginBottom: 20,
                  border: "1px solid var(--slate-200)",
                  borderRadius: "var(--radius-lg)",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                {/* Table Header / Counter */}
                <div
                  style={{
                    backgroundColor: "var(--slate-100)",
                    padding: "12px 18px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid var(--slate-200)",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)", textTransform: "uppercase" }}>
                      Purchased Items
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: "#fff",
                        color: "var(--primary)",
                        border: "1px solid var(--primary-light)",
                        padding: "2px 8px",
                        borderRadius: 12,
                      }}
                    >
                      {generatedRows.length} Line Rows ({totalPhysicalUnits} Total Units)
                    </span>
                  </div>
                  <span style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontStyle: "italic" }}>
                    * Bulk items (RAM, NVME, Mouse, Pad, Bag, Software) track quantity count & prices. Serial numbers are not required.
                  </span>
                </div>

                {/* Interactive Table of All Generated Rows */}
                <div style={{ overflowX: "auto", minHeight: generatedRows.length > 0 ? 320 : "auto", paddingBottom: generatedRows.length > 0 ? 110 : 0 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#FAFAFA", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                        <th style={{ padding: "10px 8px", width: 35, textAlign: "center", color: "var(--slate-600)", fontWeight: 700 }}>#</th>
                        <th style={{ padding: "10px 8px", minWidth: 150, color: "var(--slate-700)", fontWeight: 700 }}>Category *</th>
                        <th style={{ padding: "10px 8px", minWidth: 130, color: "var(--slate-700)", fontWeight: 700 }}>Subcategory / Spec</th>
                        <th style={{ padding: "10px 8px", minWidth: 230, color: "var(--slate-700)", fontWeight: 700 }}>Item Name *</th>
                        <th style={{ padding: "10px 8px", width: 85, minWidth: 75, color: "#065F46", fontWeight: 800, textAlign: "center" }}>Count / Qty *</th>
                        <th style={{ padding: "10px 8px", width: 135, minWidth: 125, color: "var(--slate-700)", fontWeight: 700 }}>Unit Cost (Rs) *</th>
                        <th style={{ padding: "10px 8px", width: 140, minWidth: 130, color: "var(--navy)", fontWeight: 800, textAlign: "right" }}>Total (Rs)</th>
                        <th style={{ padding: "10px 8px", minWidth: 140, color: "var(--slate-700)", fontWeight: 700 }}>
                          Serial Number <span style={{ fontSize: "0.7rem", color: "var(--slate-400)", fontWeight: 400 }}>(Hardware)</span>
                        </th>
                        <th style={{ padding: "10px 8px", minWidth: 105, color: "var(--slate-700)", fontWeight: 700 }}>Part Number</th>
                        <th style={{ padding: "10px 8px", minWidth: 105, color: "var(--slate-700)", fontWeight: 700 }}>SKU</th>
                        <th style={{ padding: "10px 8px", minWidth: 120, color: "var(--slate-700)", fontWeight: 700 }}>Warranty Start</th>
                        <th style={{ padding: "10px 8px", minWidth: 120, color: "var(--slate-700)", fontWeight: 700 }}>Warranty End</th>
                        <th style={{ padding: "10px 8px", width: 80, color: "var(--slate-700)", fontWeight: 700, textAlign: "center" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={13}
                            style={{
                              textAlign: "center",
                              padding: "40px 20px",
                              backgroundColor: "#F8FAFC",
                              color: "var(--slate-500)",
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                              <FiBox size={30} style={{ color: "var(--slate-400)" }} />
                              <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--navy)" }}>
                                Item list is empty
                              </span>
                              <span style={{ fontSize: "0.82rem", color: "var(--slate-400)" }}>
                                Click "+ Add Item Row" above or use category buttons to add items.
                              </span>
                              <button
                                type="button"
                                onClick={() => handleAddGeneratedRow()}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "8px 18px",
                                  borderRadius: 8,
                                  border: "none",
                                  backgroundColor: "var(--primary)",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: "0.82rem",
                                  cursor: "pointer",
                                  marginTop: 6,
                                }}
                              >
                                <FiPlus size={14} /> Add First Item Row
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        generatedRows.map((row, idx) => {
                          const rowCat = categories.find((c) => String(c.id) === String(row.category_id));
                          const rowSubs = rowCat?.values && rowCat.values.length > 0
                            ? rowCat.values
                            : subcategories.filter((s) => s.category_id === row.category_id);

                          const isBulk = isBulkCategory(row.category_id, row.category_name);
                          const rowQty = isBulk ? Math.max(1, parseInt(row.quantity) || 1) : 1;
                        const rowUnitPrice = parseFloat(row.unit_price) || 0;
                        const rowTotal = rowQty * rowUnitPrice;

                        return (
                          <tr
                            key={row.rowId || idx}
                            style={{
                              borderBottom: "1px solid var(--slate-100)",
                              backgroundColor: idx % 2 === 0 ? "#fff" : "var(--slate-50)",
                            }}
                          >
                            {/* # Index */}
                            <td style={{ padding: "8px 8px", textAlign: "center", fontWeight: 700, color: "var(--slate-500)" }}>
                              {idx + 1}
                            </td>

                            {/* 1. Category Dropdown */}
                            <td style={{ padding: "8px 8px" }}>
                              <select
                                value={row.category_id || ""}
                                onChange={(e) => handleRowCategoryChange(idx, e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  color: "var(--navy)",
                                  backgroundColor: "#fff",
                                }}
                              >
                                {categories.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name} {c.is_serialized === 0 ? "(Bulk)" : ""}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* 2. Subcategory / Spec Dropdown */}
                            <td style={{ padding: "8px 8px" }}>
                              <select
                                value={row.subcategory_id || ""}
                                onChange={(e) => handleRowSubcategoryChange(idx, e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.82rem",
                                  backgroundColor: "#fff",
                                }}
                              >
                                {rowSubs.map((s) => (
                                  <option key={s.id || s.name} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* 3. Item Name (Searchable according to Category & Subcategory + Free New Item Name) */}
                            <td style={{ padding: "8px 8px", minWidth: 230, position: "relative" }}>
                              <ItemNameAutocomplete
                                value={row.item_name || ""}
                                onChange={(val) => handleRowChange(idx, "item_name", val)}
                                onSelectItem={(selectedItem) => handleSelectItemForRow(idx, selectedItem)}
                                categoryId={row.category_id}
                                categoryName={row.category_name || rowCat?.name}
                                subcategoryId={row.subcategory_id}
                                subcategoryName={
                                  row.value_name ||
                                  rowSubs?.find((s) => String(s.id) === String(row.subcategory_id))?.name
                                }
                                knownCatalog={knownItemsCatalog}
                                placeholder="Type or search item name..."
                                hasError={!row.item_name}
                              />
                            </td>

                            {/* 4. Count / Quantity */}
                            <td style={{ padding: "8px 8px", width: 85, minWidth: 75, textAlign: "center" }}>
                              {isBulk ? (
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  required
                                  value={row.quantity || 1}
                                  onChange={(e) =>
                                    handleRowChange(idx, "quantity", Math.max(1, parseInt(e.target.value) || 1))
                                  }
                                  style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    padding: "6px 4px",
                                    borderRadius: 6,
                                    border: "1.5px solid #059669",
                                    backgroundColor: "#ECFDF5",
                                    fontSize: "0.84rem",
                                    fontWeight: 800,
                                    color: "#065F46",
                                    textAlign: "center",
                                  }}
                                  title="Enter count / quantity purchased"
                                />
                              ) : (
                                <div
                                  style={{
                                    padding: "6px 6px",
                                    borderRadius: 6,
                                    backgroundColor: "#EFF6FF",
                                    border: "1px solid #BFDBFE",
                                    fontSize: "0.82rem",
                                    fontWeight: 800,
                                    color: "#1D4ED8",
                                    textAlign: "center",
                                  }}
                                  title="1 unit per serialized asset row"
                                >
                                  1
                                </div>
                              )}
                            </td>

                            {/* 5. Purchase Unit Cost */}
                            <td style={{ padding: "8px 8px", width: 135, minWidth: 125 }}>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={row.unit_price}
                                onChange={(e) => handleRowChange(idx, "unit_price", e.target.value)}
                                style={{
                                  width: "100%",
                                  boxSizing: "border-box",
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: !row.unit_price ? "1.5px solid #F59E0B" : "1px solid var(--slate-300)",
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  color: "var(--navy)",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </td>

                            {/* 6. Total Amount */}
                            <td style={{ padding: "8px 8px", width: 140, minWidth: 130, textAlign: "right" }}>
                              <div
                                style={{
                                  fontWeight: 800,
                                  color: "var(--navy)",
                                  fontSize: "0.85rem",
                                  padding: "6px 10px",
                                  backgroundColor: "#F8FAFC",
                                  borderRadius: 6,
                                  whiteSpace: "nowrap",
                                  border: "1px solid var(--slate-200)",
                                }}
                              >
                                Rs. {rowTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </td>

                            {/* 8. Serial Number (Hardware only or optional bulk) */}
                            <td style={{ padding: "8px 8px" }}>
                              <input
                                type="text"
                                placeholder={isBulk ? "Optional (Bulk)" : "e.g. SN-9901"}
                                value={row.serial_number || ""}
                                onChange={(e) => handleRowChange(idx, "serial_number", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.82rem",
                                  fontFamily: "monospace",
                                  backgroundColor: isBulk && !row.serial_number ? "#F8FAFC" : "#fff",
                                }}
                                title={isBulk ? "Optional serial number for bulk item" : "Unique hardware serial number"}
                              />
                            </td>

                            {/* 9. Part Number */}
                            <td style={{ padding: "8px 8px" }}>
                              <input
                                type="text"
                                placeholder="Optional"
                                value={row.part_number || ""}
                                onChange={(e) => handleRowChange(idx, "part_number", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.82rem",
                                  fontFamily: "monospace",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </td>

                            {/* 10. SKU Number */}
                            <td style={{ padding: "8px 8px" }}>
                              <input
                                type="text"
                                placeholder="Auto if empty"
                                value={row.sku || ""}
                                onChange={(e) => handleRowChange(idx, "sku", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.82rem",
                                  fontFamily: "monospace",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </td>

                            {/* 11. Warranty Start Date */}
                            <td style={{ padding: "8px 8px" }}>
                              <input
                                type="date"
                                value={row.warranty_start_date}
                                onChange={(e) => handleRowChange(idx, "warranty_start_date", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "5px 6px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.8rem",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </td>

                            {/* 12. Warranty End Date */}
                            <td style={{ padding: "8px 8px" }}>
                              <input
                                type="date"
                                value={row.warranty_end_date}
                                onChange={(e) => handleRowChange(idx, "warranty_end_date", e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "5px 6px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-300)",
                                  fontSize: "0.8rem",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </td>

                            {/* Row Actions */}
                            <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                              <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
                                <button
                                  type="button"
                                  onClick={() => handleDuplicateRow(idx)}
                                  style={{
                                    padding: "5px 7px",
                                    borderRadius: 6,
                                    backgroundColor: "var(--primary-light, #EEF2FF)",
                                    color: "var(--primary, #4F46E5)",
                                    border: "1px solid var(--primary-light, #C7D2FE)",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Copy this row (without serial number)"
                                >
                                  <FiCopy size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteGeneratedRow(idx)}
                                  style={{
                                    padding: "5px 7px",
                                    borderRadius: 6,
                                    backgroundColor: "#FEE2E2",
                                    color: "#DC2626",
                                    border: "1px solid #FECACA",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Delete this row"
                                >
                                  <FiTrash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid var(--slate-200)" }}>
                        <td colSpan={4} style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--slate-600)", fontSize: "0.85rem" }}>
                          Total Lines: <strong>{generatedRows.length}</strong>
                        </td>
                        <td style={{ padding: "10px 8px", fontWeight: 800, color: "#065F46", fontSize: "0.88rem", textAlign: "center" }}>
                          {totalPhysicalUnits} Units
                        </td>
                        <td colSpan={2} style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: "var(--slate-600)", fontSize: "0.85rem" }}>
                          Total Amount:
                        </td>
                        <td style={{ padding: "10px 8px", fontWeight: 900, color: "var(--navy)", fontSize: "0.95rem", textAlign: "right" }}>
                          Rs. {totalPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td colSpan={6}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Purchase Summary & Final Action Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "20px 24px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 20,
            }}
          >
            {/* Total Items Count & Total Price Display */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              {/* Total Item Count Card */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  backgroundColor: "var(--slate-50)",
                  padding: "10px 18px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-200)",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: "var(--primary-light, #ECFDF5)",
                    color: "var(--primary, #064E3B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                  }}
                >
                  #
                </div>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Total Items / Units
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--navy)" }}>
                    {totalPhysicalUnits} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--slate-500)" }}>Units ({totalItemCount} lines)</span>
                  </div>
                </div>
              </div>

              {/* Total Price Card */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  backgroundColor: "#F0FDF4",
                  padding: "10px 18px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid #BBF7D0",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: "#DCFCE7",
                    color: "#166534",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.95rem",
                  }}
                >
                  Rs
                </div>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "#166534", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Total Purchase Price
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#15803D" }}>
                    Rs. {totalPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor & Record Purchase Button */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {currentVendor && (
                <div style={{ textAlign: "right", fontSize: "0.84rem", color: "var(--slate-600)" }}>
                  <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", textTransform: "uppercase", fontWeight: 700 }}>
                    Vendor Supplier
                  </div>
                  <div style={{ fontWeight: 800, color: "var(--navy)" }}>{currentVendor.company_name}</div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || generatedRows.length === 0}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 28px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: editingPurchase ? "#2563EB" : "var(--primary)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  boxShadow: "var(--shadow-md)",
                  transition: "all 0.15s ease",
                }}
              >
                <FiCheckCircle size={18} />
                {submitting
                  ? (editingPurchase ? "Updating Bill..." : "Recording...")
                  : (editingPurchase ? `Update Purchase Bill (#${editingPurchase.originalBillNumber})` : `Record Purchase `)}
              </button>
            </div>
          </div>
        </form>
      ) : activeTab === "history" ? (
        /* PURCHASE HISTORY VIEW */
        <div>
          {/* Table Container */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            {/* Toolbar */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--slate-200)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                backgroundColor: "var(--slate-50)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 340px", maxWidth: 540 }}>
                <div style={{ position: "relative", width: "100%" }}>
                  <FiSearch
                    size={16}
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--slate-400)",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search by Bill No, Date, Vendor Company, Contact, NTN, Item, Serial..."
                    value={historySearch}
                    onChange={(e) => {
                      setHistorySearch(e.target.value);
                    }}
                    style={{
                      width: "100%",
                      height: 38,
                      padding: "7px 32px 7px 36px",
                      borderRadius: 8,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      fontSize: "0.85rem",
                      boxSizing: "border-box",
                    }}
                  />
                  {historySearch && (
                    <button
                      type="button"
                      onClick={() => setHistorySearch("")}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--slate-400)",
                        padding: 3,
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Clear search"
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => fetchPurchaseHistory(historyPage, historyPageSize, debouncedHistorySearch)}
                  disabled={historyLoading}
                  style={{
                    height: 38,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-700)",
                    cursor: historyLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                  title="Refresh Purchase History"
                >
                  <FiRefreshCw size={14} style={{ animation: historyLoading ? "spin 1s linear infinite" : "none" }} />
                  Refresh
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "0.82rem", color: "var(--slate-600)", fontWeight: 600 }}>
                  Showing {historyPurchases.length} of {historyPagination.total || 0} {historyPagination.total === 1 ? "Bill" : "Bills"}
                  {debouncedHistorySearch && <span style={{ color: "var(--primary)", marginLeft: 4 }}>(filtered)</span>}
                </span>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, width: 50 }}>#</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Bill No</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Date</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Company Name</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Total Items</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Total Amount</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 45, textAlign: "center", color: "var(--slate-500)" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
                          <FiRefreshCw size={18} style={{ color: "var(--primary)", animation: "spin 1s linear infinite" }} />
                          <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>Loading purchase bills from server...</span>
                        </div>
                      </td>
                    </tr>
                  ) : historyPurchases.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        No purchase bills found matching your search.
                      </td>
                    </tr>
                  ) : (
                    historyPurchases.map((bill, idx) => {
                      return (
                        <tr
                          key={bill.bill_number + "-" + idx}
                          onClick={() => handleViewInvoice(bill)}
                          style={{
                            borderBottom: "1px solid var(--slate-100)",
                            cursor: "pointer",
                            transition: "background 0.15s ease",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          title="Click to view full printable invoice for this bill"
                        >
                          <td style={{ padding: "14px 18px", color: "var(--slate-400)", fontWeight: 600, fontSize: "0.82rem" }}>
                            {historyStartIndex + idx}
                          </td>
                          <td style={{ padding: "14px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  backgroundColor: "#EFF6FF",
                                  color: "#2563EB",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <FiFileText size={15} />
                              </div>
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  color: "#1D4ED8",
                                  fontSize: "0.86rem",
                                }}
                              >
                                {bill.display_bill_number}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "14px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-700)", fontWeight: 600, fontSize: "0.84rem" }}>
                              <FiCalendar size={14} style={{ color: "var(--slate-400)", flexShrink: 0 }} />
                              <span>{formatDateDisplay(bill.purchase_date)}</span>
                            </div>
                          </td>
                          <td style={{ padding: "14px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <FiTruck size={15} style={{ color: "var(--primary)", flexShrink: 0 }} />
                              <div>
                                <span style={{ fontWeight: 700, color: "var(--navy)", fontSize: "0.88rem" }}>
                                  {bill.vendor_name}
                                </span>
                                {bill.vendor_contact_person && (
                                  <div style={{ fontSize: "0.74rem", color: "var(--slate-500)" }}>
                                    {bill.vendor_contact_person}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "14px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "3px 10px",
                                  borderRadius: 12,
                                  backgroundColor: "var(--slate-100)",
                                  color: "var(--slate-700)",
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                }}
                              >
                                <FiBox size={13} style={{ color: "var(--primary)" }} />
                                {bill.total_quantity ?? bill.total_items_count ?? 0} {(bill.total_quantity ?? bill.total_items_count) === 1 ? "Item" : "Items"}
                              </span>
                              {(parseFloat(bill.returned_items ?? bill.total_returned_items_count) || 0) > 0 && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "2px 7px",
                                    borderRadius: 8,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    backgroundColor: "#FEF2F2",
                                    color: "#DC2626",
                                    border: "1px solid #FEE2E2",
                                  }}
                                  title={`${bill.returned_items ?? bill.total_returned_items_count} item(s) returned to vendor`}
                                >
                                  <FiRotateCcw size={11} /> -{bill.returned_items ?? bill.total_returned_items_count} Ret
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: "14px 18px" }}>
                            <span style={{ fontWeight: 800, color: "#15803D", fontSize: "0.9rem" }}>
                              Rs. {(parseFloat(bill.total_price ?? bill.total_amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            {(parseFloat(bill.returned_amount ?? bill.total_returned_amount) || 0) > 0 && (
                              <div style={{ fontSize: "0.72rem", color: "#DC2626", fontWeight: 600, marginTop: 2 }}>
                                Ret: Rs. {(parseFloat(bill.returned_amount ?? bill.total_returned_amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "14px 18px", textAlign: "right" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoice(bill);
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  border: "1px solid var(--primary)",
                                  backgroundColor: "var(--primary-light)",
                                  color: "var(--primary)",
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                title="Open full-page printable invoice"
                              >
                                <FiEye size={14} /> View Invoice
                              </button>

                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSearchParams({ tab: "record", editBill: bill.bill_number });
                                    loadBillForEdit(bill.bill_number || bill.id, bill);
                                  }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "6px 12px",
                                    borderRadius: 6,
                                    border: "1px solid #93C5FD",
                                    backgroundColor: "#EFF6FF",
                                    color: "#1D4ED8",
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                  }}
                                  title="Edit bill details, vendor account, and prices"
                                >
                                  <FiEdit size={14} /> Edit
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBillToDelete(bill);
                                  setIsDeleteModalOpen(true);
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #FCA5A5",
                                  backgroundColor: "#FEF2F2",
                                  color: "#DC2626",
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                title="Delete bill and all its related items"
                              >
                                <FiTrash2 size={14} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Purchase History */}
            {historyPagination.total > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: "14px 20px",
                  borderTop: "1px solid var(--slate-200)",
                  backgroundColor: "var(--slate-50)",
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{historyStartIndex}</strong> to <strong>{historyEndIndex}</strong> of{" "}
                  <strong>{historyPagination.total}</strong> {historyPagination.total === 1 ? "bill" : "bills"}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {/* Rows Per Page Selector */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Rows:</span>
                    <select
                      value={historyPageSize}
                      onChange={(e) => {
                        setHistoryPageSize(Number(e.target.value));
                        setHistoryPage(1);
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.82rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                        color: "var(--navy)",
                        cursor: "pointer",
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Prev Button */}
                  <button
                    type="button"
                    disabled={historyPage <= 1 || historyLoading}
                    onClick={() => setHistoryPage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: historyPage <= 1 ? "var(--slate-100)" : "#fff",
                      color: historyPage <= 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: historyPage <= 1 ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FiChevronLeft size={15} /> Prev
                  </button>

                  {/* Numbered Page Buttons */}
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {Array.from({ length: totalHistoryPages }, (_, i) => i + 1)
                      .filter((p) => {
                        if (totalHistoryPages <= 7) return true;
                        if (p === 1 || p === totalHistoryPages) return true;
                        if (Math.abs(p - historyPage) <= 1) return true;
                        return false;
                      })
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const showEllipsis = prev && p - prev > 1;

                        return (
                          <React.Fragment key={p}>
                            {showEllipsis && (
                              <span style={{ padding: "0 4px", color: "var(--slate-400)" }}>...</span>
                            )}
                            <button
                              type="button"
                              disabled={historyLoading}
                              onClick={() => setHistoryPage(p)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: p === historyPage ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                                backgroundColor: p === historyPage ? "var(--primary)" : "#fff",
                                color: p === historyPage ? "#fff" : "var(--slate-700)",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    disabled={historyPage >= totalHistoryPages || historyLoading}
                    onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: historyPage >= totalHistoryPages ? "var(--slate-100)" : "#fff",
                      color: historyPage >= totalHistoryPages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: historyPage >= totalHistoryPages ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next <FiChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* PURCHASE RETURNS VIEW */
        <PurchaseReturnsTab vendors={vendors} purchases={purchases} onReturnChange={() => fetchData(true)} />
      )}

      {/* CONFIRM DELETE MODAL: Delete bill and all its items */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => !deleting && setIsDeleteModalOpen(false)} maxWidth={480}>
        {billToDelete && (
          <div style={{ padding: "6px 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  backgroundColor: "#FEE2E2",
                  color: "#DC2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FiTrash2 size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "var(--navy)" }}>
                  Delete Purchase Bill & Items
                </h3>
                <p style={{ margin: "2px 0 0 0", fontSize: "0.82rem", color: "var(--slate-500)" }}>
                  Bill #{billToDelete.bill_number}
                </p>
              </div>
            </div>

            <div
              style={{
                padding: "14px 16px",
                backgroundColor: "#FEF2F2",
                borderRadius: 8,
                border: "1px solid #FCA5A5",
                fontSize: "0.85rem",
                color: "#991B1B",
                lineHeight: 1.5,
                marginBottom: 18,
              }}
            >
              Are you sure you want to permanently delete purchase bill{" "}
              <strong>#{billToDelete.bill_number}</strong>?
              <br />
              <br />
              <strong>Warning:</strong> This will delete this bill and all{" "}
              <strong>{billToDelete.items.length} inventory item(s)</strong> created under it.
              <br />
              <small style={{ color: "#B91C1C", display: "block", marginTop: 4 }}>
                * If any item from this bill has already been sold, deletion will be blocked by the system to maintain
                inventory integrity.
              </small>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setIsDeleteModalOpen(false)}
                style={{
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleConfirmDelete}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "none",
                  backgroundColor: "#DC2626",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                <FiTrash2 size={15} />
                {deleting ? "Deleting..." : "Yes, Delete Bill & Items"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PurchasesPage;
